/**
 * Canonical presence / agent status colors for indicators (aligned with
 * teamscx-realtime-report's Overview "Agent status" palette so both apps show
 * the same status color for the same agent). Cloned copy — each project
 * keeps its own so neither depends on the other's build.
 */
/** Fallback when status is empty / unresolved. */
const PRESENCE_UNKNOWN_DOT = "#011627"

const OFFLINE_COLOR_DARK = "#6D8596"
const OFFLINE_NAMES = new Set(["offline", "off work"])

export const AGENT_STATUS_LEGEND_ITEMS = [
  { name: "Available", dotColor: "#00FEC1" },
  { name: "Busy", dotColor: "#E71D36" },
  /** Same token as "Off work" on the reference palette (#23407a). */
  { name: "Offline", dotColor: "#23407A" },
  { name: "Presence unknown", dotColor: PRESENCE_UNKNOWN_DOT },
  { name: "Do not disturb", dotColor: "#E71D36" },
  { name: "Away", dotColor: "#FFBB28" },
  { name: "Break", dotColor: "#FF8042" },
  { name: "Be right back", dotColor: "#FF9F1C" },
  /** Same red family as Busy / DND / internal call on the palette sheet. */
  { name: "In a meeting", dotColor: "#E71D36" },
  { name: "In a call", dotColor: "#1681F6" },
  { name: "Off work", dotColor: "#23407A" },
  { name: "In an internal call", dotColor: "#E71D36" },
  { name: "Out of office", dotColor: "#7B2CBF" },
] as const

const dotColorByNormalizedName = new Map(AGENT_STATUS_LEGEND_ITEMS.map((item) => [item.name.toLowerCase(), item.dotColor]))

function normalizeAgentStatusLookupKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
}

/**
 * Maps API keys / labels (e.g. `away`, `AWAY`, `doNotDisturb`) to the same display names as
 * {@link AGENT_STATUS_LEGEND_ITEMS}.
 */
export function canonicalAgentStatusDisplayName(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return "—"
  const norm = normalizeAgentStatusLookupKey(trimmed)
  const hit = AGENT_STATUS_LEGEND_ITEMS.find((item) => item.name.toLowerCase() === norm)
  if (hit) return hit.name
  /** Backend often sends JSON keys with no spaces (`presenceunknown`, `donotdisturb`) — match legend by compact form. */
  const compact = norm.replace(/\s+/g, "")
  if (compact.length > 0) {
    const hitCompact = AGENT_STATUS_LEGEND_ITEMS.find(
      (item) => item.name.toLowerCase().replace(/\s+/g, "") === compact,
    )
    if (hitCompact) return hitCompact.name
  }
  const spaced = trimmed.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim()
  if (!spaced) return trimmed
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

/** Extra labels not in the Teams list but used elsewhere. */
const extraStatusColors: Readonly<Record<string, string>> = {
  unavailable: "#E71D36",
  focus: "#E71D36",
  focusing: "#E71D36",
}

/**
 * Returns the same hex as teamscx-realtime-report's "Agent status" widget for a given status label.
 * Unknown values fall back to "Presence unknown" color.
 */
export function getAgentStatusDotColor(status: string, isDark = false): string {
  const trimmed = status.trim()
  if (!trimmed) return PRESENCE_UNKNOWN_DOT
  const displayName = canonicalAgentStatusDisplayName(trimmed)
  if (displayName === "—") return PRESENCE_UNKNOWN_DOT
  const byLegend = dotColorByNormalizedName.get(displayName.toLowerCase())
  if (byLegend) {
    if (isDark && OFFLINE_NAMES.has(displayName.toLowerCase())) return OFFLINE_COLOR_DARK
    return byLegend
  }
  const rawLower = trimmed.toLowerCase()
  return extraStatusColors[rawLower] ?? PRESENCE_UNKNOWN_DOT
}
